/**
	* Tool picker view
	* Handles: toolbar buttons
	* @module ToolPicker
*/

/// Imports

// React
import { useState, useEffect } from 'react';

// OpenLayers
import OlMap from 'ol/Map';

// Models
import MapToolMode from 'lib/enums/maptoolmode';
import MapToolModeOption from 'lib/classes/maptoolmodeoption';
import { Popup } from 'lib/classes/popup';

// Tools
import { getModeDescription, getToolEnabled } from 'lib/utilities/toolmodes';
import { toolKeybinds, getToolModeByName } from 'lib/utilities/toolmodes';
import { assetURL } from 'lib/utilities/asset';

// UI
import EntryPopup from 'views/popups/Entry';
import PrintPopup from 'views/popups/Print';
import LocationsPopup from 'views/popups/Locations';
import AddressSearchPopup from 'views/popups/AddressSearch';
import UploadToolPopup from 'views/popups/Upload';
import FilterPopup from 'views/popups/Filter';
import DrawPortalPopup from 'views/popups/DrawPortal';
import LinkedResourcesPopup from 'views/popups/LinkedResourcesPopup';

// Popups
import ToolSelect from 'views/popups/ToolSelect';
import Guide from 'views/popups/Guide';
import Accessibility from 'views/popups/Accessibility';
import PermalinkPopup from 'views/popups/Permalink';

// jQuery
import $ from 'jquery';

// Lang
import { getLocaleString } from 'lib/localization/vendor';

// Styles
import 'styles/css/ToolPicker.scss';
import 'styles/css/ZoomBar.scss';

/**
	* Tool picker react component props
	* 
	* @param currentToolMode	-	Current map tool mode (passed by views/Tools)
	* @param onChangeTool		-	Map tool change handler (passed by views/Tools)
*/
interface ToolPickerProps {
	olMap:OlMap,
	currentToolMode:MapToolMode,
	onChangeTool:Function
}

/**
	* Draw options param interface
	*
	* @param options - Map tool mode options
	* @param isPopup - Is this drawing inside of the tool select popup?
*/
interface DrawOptionsParams {
	options:MapToolModeOption[],
	isPopup?:boolean
}

/**
	* Draw option param interface
	*
	* @param option - Map tool mode option
	* @param isPopup - Is this drawing inside of the tool select popup?
*/
interface DrawOptionParams {
	option:MapToolModeOption,
	isPopup?:boolean,
	relativeTabIndex:number
}

/**
	* Get map tools options interface
	* 
	* @param isPopup - Are these options displaying inside of the tool select popup?
*/
interface GetMapToolOptionsParams {
	isPopup?:boolean
}

var mapConfig:any;

function ToolPicker({olMap, currentToolMode, onChangeTool}:ToolPickerProps) {
	mapConfig = olMap.get('opus-config');
	
	const [rightPanelOpen, setRightPanelOpen] = useState<boolean>(false);
	const [toolsMaster, setToolsMaster] = useState<MapToolMode[]>(null); // Only for the master url tool override
	
	const selected:string = MapToolMode[currentToolMode];
	
	// Initialize
	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);
		
		_events.on('right-panel-update', (open:boolean) => {
			setTimeout(() => setRightPanelOpen(open), 100);
		});
		_events.on('open-upload-popup', () => {
			if(_ui.isPopupOpen(UploadToolPopup)) return;
			
			UploadToolPopup.set('map', olMap);
			
			_ui.showPopup(UploadToolPopup);
		});
		_events.on('set-tools-master', () => {
			setToolsMaster(_config.toolsMaster != null ? [..._config.toolsMaster] : null);
		});
		
		_events.on('tool-mode', (mode:MapToolMode) => {
			const options:MapToolModeOption[] = getMapToolOptions({isPopup: true});
			const option:MapToolModeOption = options.find((opt:MapToolModeOption) => opt.mapToolMode == mode);
			if(!option) {
				console.warn('No option for mode', mode);
				return;
			}
			(option.onClickOverride || onChangeTool)(option.mapToolMode);
		});
		_events.on('window:activate-tool', (event:MessageEvent) => {
			const payload:any = event.data.payload;
			const toolModeName:string = payload.toolModeName;
			
			const mapToolMode:MapToolMode = getToolModeByName(toolModeName);
			if(mapToolMode)
				_ui.activateToolMode(mapToolMode);
			else
				console.warn('Unknown tool mode to activate', toolModeName);
		});
		
		_events.invoke('set-tools-master');
	}, []);
	
	// Rebuild tooltip when document elements change
	useEffect(() => _ui.tooltip.rebuild(), [currentToolMode, _dimensions]);
	
	/**
		* Draw options
		* 
		* @param {MapToolModeOption[]|boolean}:DrawOptionsParams [options, isPopup] - Options to draw | is it inside tool select popup?
	*/
	const drawOptions = ({options, isPopup}:DrawOptionsParams) => {
		const items = [];
		
		for(let i in options) {
			const option:MapToolModeOption = options[i];
			
			items.push(
				drawOption({
					option: option, 
					isPopup: isPopup,
					relativeTabIndex: items.length
				})
			);
		}
		
		return items;
	}
	
	/**
		* Draw map tool mode button
		* 
		* @param {MapToolModeOption|boolean}:DrawOptionParams [option, isPopup] - Option to draw | is it inside tool select popup?
	*/
	const drawOption = ({option, isPopup, relativeTabIndex}:DrawOptionParams) => {
		const toolMode:MapToolMode = option.mapToolMode;
		const onClickOverride:Function = option.onClickOverride;
		const name:string = MapToolMode[toolMode];
		const containerClass:string = `tool-option-container${isPopup ? ' tool-option-container--mobile' : ''} tool-option-container--${name} prevent-select`;
		const childClass:string = `tool-option${option.isSelected(currentToolMode) ? ` tool-option--selected ${_ui.getSelClassName()}` : ''} prevent-select`;
		const iconSource:string = `assets/images/${name.toLowerCase()}.png`;
		const dataTip:string = !isPopup ? getModeDescription(toolMode, true) : '';
		
		const onClickMaster:Function = () => {
			const callback:Function = onClickOverride || onChangeTool;
			
			_ui.tooltip.hide();
			
			if(isPopup) _ui.closePopupOf(ToolSelect);
			
			callback(toolMode);
		};
		const onClick:any = () => onClickMaster();
		
		_ui.setMapToolModeClickMaster(toolMode, onClickMaster);
		
		return (
			<div 
				className={containerClass} 
				onClick={onClick}
				onKeyDown={(e:any) => {
					if (e.key === 'Enter')
						onClick();
				}}
				role='button'
				data-delay-show='200' 
				data-place='right' 
				data-tip={dataTip} 
				key={toolMode}
				tabIndex={relativeTabIndex + 1}
			>
				<div className={childClass}>
					<span className='filler'></span><img src={assetURL(iconSource)} />
				</div> 
				{
					isPopup && (
						<span className='tool-option__label'>
							{getModeDescription(toolMode)}
						</span>
					)
				}
			</div>
		);
	}
	
	/**
		* Open popup tool selector ([...] button)
		* 
		* @remarks
		* This will open a popup with all of the map tool options.
		* The unique draw method is passed to the popup with a custom tool button
		* onClick callback which includes closing the popup when a tool is selected.
	*/
	const openToolSelect = () => {
		ToolSelect.set('draw-options', () => drawOptions( {options: getMapToolOptions( {isPopup: true} ), isPopup: true} ));
		
		_ui.showPopup(ToolSelect);
	}
	
	/**
		* Open entry popup
		* 
		* @remarks
		* This will open the entry popup, mainly for testing purposes only.
		* The entry popup configures the map ID and client ID.
	*/
	const openEntryPopup = () => {
		EntryPopup.set('mapId', _config.mapId);
		EntryPopup.set('clientId', _config.clientId);
		
		EntryPopup.closeable = true;
		
		_ui.showPopup(EntryPopup);
	}
	
	/**
		* Open print popup
		* 
		* @remarks
		* This will open the print map popup
	*/
	const openPrintPopup = () => {
		PrintPopup.set('map', olMap);
		
		_ui.showPopup(PrintPopup);
	}
	
	/**
		* Open permalink popup
		* 
		* @remarks
		* This will open the permalink popup
	*/
	const openPermalinkPopup = () => {
		PermalinkPopup.set('opus-config', mapConfig);
		
		_ui.showPopup(PermalinkPopup);
	}
	
	/**
		* Open the user guide popup
		* 
		* @remarks
		* This popup will open the user guide
	*/
	const openGuide = () => {
		Guide.set('map', olMap);
		
		_ui.showPopup(Guide);
	}
	
	/**
		* Open the accessibility popup
		* 
		* @remarks
		* This will open the accessibility configuration options
	*/
	const openAccessibility = () => {
		Accessibility.set('map-config', mapConfig);
		
		_ui.showPopup(Accessibility);
	}
	
	/**
		* Open the linked resources popup
		* 
		* @remarks
		* The popup will open the linked resources
	 */
	const openLinkedResources = () => {
		LinkedResourcesPopup.set('opus-config', mapConfig);
		
		_ui.showPopup(LinkedResourcesPopup);
	}
	
	/**
		* Can show the tool picker panel
		* 
		* @returns {boolean} can draw the panel
	*/
	const canDraw = ():boolean => {
		return !(_dimensions.width < 375 && _ui.getRightPanelOpen());
	}
	
	/**
		* Get an array of all the available map tool modes for this map
		* 
		* @param {boolean}:GetMapToolOptionsParams [isPopup=false] - Inside tool select popup?
	*/
	const getMapToolOptions = ({isPopup}:GetMapToolOptionsParams = {isPopup: false}):MapToolModeOption[] => {
		const options:MapToolModeOption[] = [];
		
		// Select tool
		if(getToolEnabled(MapToolMode.Select, mapConfig)) {
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.Select
				})
			);
		}
		
		// Include the locations search tools if config has locations or sites in it
		if(getToolEnabled(MapToolMode.Locations, mapConfig)) {
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.Locations,
					onClickOverride: (toolMode:MapToolMode) => {
						if(_ui.isPopupOpen(LocationsPopup)) return;
						
						LocationsPopup.set('map', olMap);
						_ui.showPopup(LocationsPopup);
					}
				})
			);
		}
		
		// Map layers tool
		if(getToolEnabled(MapToolMode.MapLayers, mapConfig)) {
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.MapLayers,
					onClickOverride: (toolMode:MapToolMode) => {
						if(mapConfig.legend.layerGroups.length < 1) {
							return _ui.showError(
								getLocaleString('nolayerserror'), 
								getLocaleString('nolayerstitle')
							);
						}
							
						onChangeTool(toolMode);
					}
				})
			);
		}
		
		// Drawing tool
		if(getToolEnabled(MapToolMode.Draw, mapConfig) || getToolEnabled(MapToolMode.Annotation, mapConfig)) {
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.Draw,
					isSelected: (currentMode:MapToolMode) => currentMode == MapToolMode.Draw || currentMode == MapToolMode.Annotation,
					onClickOverride: (toolMode:MapToolMode) => {
						DrawPortalPopup.set('map-config', mapConfig);
						
						!_ui.isPopupOpen(DrawPortalPopup) && _ui.showPopup(DrawPortalPopup);
					}
				})
			);
		}
		
		// Upload tool
		if(getToolEnabled(MapToolMode.Upload, mapConfig)) {
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.Upload,
					// No longer change the tool mode to upload tool, only open popup
					onClickOverride: (toolMode:MapToolMode) => {
						if(_ui.isPopupOpen(UploadToolPopup)) return;
						
						UploadToolPopup.set('map', olMap);
						_ui.showPopup(UploadToolPopup);
					}
				})
			);
		}
		
		// Measure tool
		if(getToolEnabled(MapToolMode.Measure, mapConfig)) {
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.Measure
				})
			);
		}
		
		// Include the address search tools if config has addressSearch enabled
		if(getToolEnabled(MapToolMode.Address, mapConfig)) {
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.Address,
					// No longer change the tool mode to address tool, only open popup
					onClickOverride: (toolMode:MapToolMode) => {
						if(_ui.isPopupOpen(AddressSearchPopup)) return;
						
						AddressSearchPopup.set('map', olMap);
						_ui.showPopup(AddressSearchPopup);
					}
				})
			);
		}
		
		if(getToolEnabled(MapToolMode.LinkedResources, mapConfig)) {
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.LinkedResources,
					onClickOverride: (toolMode:MapToolMode) => openLinkedResources()
				})
			);
		}
		
		if(!isPopup) {
			// Include only if not in tool select popup
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.More,
					onClickOverride: () => openToolSelect()
				})
			);
		}else{
			// Include the address search tools if config has addressSearch enabled
			if(getToolEnabled(MapToolMode.Filter, mapConfig)) {
				options.push(
					new MapToolModeOption({
						mapToolMode: MapToolMode.Filter,
						onClickOverride: (toolMode:MapToolMode) => {
							if(_ui.isPopupOpen(FilterPopup)) return;
							
							FilterPopup.set('map', olMap);
							
							_ui.showPopup(FilterPopup);
						}
					})
				);
			}
			
			// Include these only if in tool select popup
			if(getToolEnabled(MapToolMode.Print, mapConfig)) {
				options.push(
					new MapToolModeOption({
						mapToolMode: MapToolMode.Print,
						onClickOverride: (toolMode:MapToolMode) => openPrintPopup()
					})
				);
			}
			
			// Include these only if in tool select popup
			if(getToolEnabled(MapToolMode.Permalink, mapConfig)) {
				options.push(
					new MapToolModeOption({
						mapToolMode: MapToolMode.Permalink,
						onClickOverride: (toolMode:MapToolMode) => openPermalinkPopup()
					})
				);
			}
			
			if(getToolEnabled(MapToolMode.Accessibility, mapConfig)) {
				options.push(
					new MapToolModeOption({
						mapToolMode: MapToolMode.Accessibility,
						onClickOverride: (toolMode:MapToolMode) => openAccessibility()
					})
				);
			}
			
			if(getToolEnabled(MapToolMode.Tutorial, mapConfig)) {
				options.push(
					new MapToolModeOption({
						mapToolMode: MapToolMode.Tutorial,
						onClickOverride: (toolMode:MapToolMode) => openGuide()
					})
				);
			}
			
			/*
			// Map selector is deprecated
			options.push(
				new MapToolModeOption({
					mapToolMode: MapToolMode.MapSelector,
					onClickOverride: () => openEntryPopup()
				})
			);
			*/
		}
		
		return options;
	}
	
	/*
		Draw the simple tool buttons (only two buttons)
	*/
	const drawSimpleTools = () => {
		return (
			<>
				{
					drawOption({
						option: new MapToolModeOption({
							mapToolMode: currentToolMode
						}),
						relativeTabIndex: 0
					})
				}
				{
					drawOption({
						option: new MapToolModeOption({
							mapToolMode: MapToolMode.More,
							onClickOverride: () => openToolSelect()
						}),
						relativeTabIndex: 1
					})
				}
			</>
		)
	}
	
	/*
		Draw all of the tool buttons
	*/
	const drawAllTools = () => {
		return (
			<>
				{drawOptions( {options: getMapToolOptions()} )}
			</>
		)
	}
	
	/*
		Draw the tool buttons
	*/
	const drawToolButtons = () => {
		// Call after draw - fade in or hide tool selector based on canDraw()
		useEffect(() => {
			if(!canDraw()) {
				$('#tool-picker').fadeOut(_ui.transitionUpOptions);
			}else{
				$('#tool-picker').fadeIn(_ui.transitionDownOptions);
			}
		});
		
		let className:string = 'tool-picker prevent-select';
		
		if(!_ui.tooSmallForZoomBar() && _ui.getZoomBarEnabled(mapConfig))
			className += ' tool-picker--after-zoom-bar';
		
		return (
			<div id='tool-picker' className={className}>
				{_dimensions.height < 495 ? drawSimpleTools() : drawAllTools()}
			</div>
		)
	}
	
	const handleKeyDown = (e:KeyboardEvent) => {
		// isPopup: true because you want all the tool options available to work
		const options:MapToolModeOption[] = getMapToolOptions({isPopup: true});
		let changeToolModeTo:MapToolMode = null;
		
		if(!_ui.isCtrlDown() && !_ui.hasFocus()) {
			switch (e.key) {
				case toolKeybinds.get(MapToolMode.Select):
					changeToolModeTo = MapToolMode.Select;
					break;
			
				case toolKeybinds.get(MapToolMode.Draw):
					changeToolModeTo = MapToolMode.Draw;
					break;
			
				case toolKeybinds.get(MapToolMode.MapLayers):
					changeToolModeTo = MapToolMode.MapLayers;
					break;
			
				case toolKeybinds.get(MapToolMode.Address):
					changeToolModeTo = MapToolMode.Address;
					break;
			
				case toolKeybinds.get(MapToolMode.Upload):
					changeToolModeTo = MapToolMode.Upload;
					break;
			
				case toolKeybinds.get(MapToolMode.Locations):
					changeToolModeTo = MapToolMode.Locations;
					break;
			
				case toolKeybinds.get(MapToolMode.Measure):
					changeToolModeTo = MapToolMode.Measure;
					break;
			
				case toolKeybinds.get(MapToolMode.Filter):
					changeToolModeTo = MapToolMode.Filter;
					break;
			
				case toolKeybinds.get(MapToolMode.Print):
					changeToolModeTo = MapToolMode.Print;
					break;
			
				case toolKeybinds.get(MapToolMode.Accessibility):
					changeToolModeTo = MapToolMode.Accessibility;
					break;
			}
			
			if(changeToolModeTo != null)
				e.preventDefault();
		}
		
		for(let option of options) {
			if(option.mapToolMode == changeToolModeTo) {
				// Call the correct onClick handler for this map tool mode as defined in getMapToolOptions
				(option.onClickOverride || onChangeTool)(option.mapToolMode);
			}
		}
	}
	
	return (
		<>
			{drawToolButtons()}
		</>
	)
}

export default ToolPicker;